import { ImageVideoPreview } from "../../../common/ImageVideoPreview";
import { AnimationPreviewWrapper } from "./AnimationPreviewWrapper";
import { DraggableComponent } from "./DraggableComponent";
import { ITEM_TYPES, MediaItem, TabKeys } from "../../../../types";
import { getMaskClipStyle, isMasked } from "../../../../helpers/maskHelper";
import { enrichAssetWithPath } from "../../../../../animation/helpers/helpers";
import {
  resolveColorToBrandkitColorIfNeeded,
  roundToHalfPixel,
} from "../../../../helpers/itemsHelper";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { generateStrokeStyles } from "../../../../helpers";
import { useCallback } from "react";
import { useTemplateData } from "../../../../hooks/useTemplateData";

export const TemplateVideos = ({
  templateVideos,
  previewWidth,
  previewHeight,
  editable = false,
}: {
  templateVideos: MediaItem[];
  previewWidth: number;
  previewHeight: number;
  editable?: boolean;
}) => {
  const { updateVideo, currentItem } = useTemplateEditorStore((state) => ({
    updateVideo: state.updateVideo,
    currentItem: state.templateState.currentItem,
  }));

  const { setActiveTab, activeTab } = useTemplatesStatusStore((state) => ({
    setActiveTab: state.setActiveTab,
    activeTab: state.activeTab,
  }));

  const {
    brandKitData: { brandKitPalette },
  } = useTemplateData();

  const getVideoComponent = useCallback(
    (video: MediaItem) => {
      const styles = generateStrokeStyles(
        {
          ...video,
          stroke: {
            ...video.stroke,
            color: video.stroke?.color
              ? resolveColorToBrandkitColorIfNeeded(
                  video.stroke.color,
                  brandKitPalette
                )
              : undefined,
          },
        },
        previewWidth,
        previewHeight
      );
      // Clip the media to the mask on the media element so the crop travels
      // with enter/leave animations; dropped while editing this item.
      const isEditingThis = editable && currentItem?.id === video.id;
      const cropStyle =
        isMasked(video.mask) && !isEditingThis
          ? getMaskClipStyle(video.mask)
          : undefined;
      return (
        <ImageVideoPreview
          type={ITEM_TYPES.Video}
          assetId={video.assetId}
          path={video.path}
          style={{ ...styles, ...cropStyle }}
          videoProps={
            editable
              ? {
                  controls: currentItem?.id === video.id,
                  playsInline: true,
                }
              : undefined
          }
        />
      );
    },
    [previewWidth, previewHeight, brandKitPalette, currentItem, editable]
  );

  if (templateVideos.length === 0) return null;

  if (!editable) {
    return (
      <>
        {templateVideos.map((video) => {
          if (!video.path) {
            video = enrichAssetWithPath(video, templateVideos);
          }

          const strokeWidth = Math.round(
            (video.stroke?.width ?? 0) * previewWidth
          );
          const doubleStrokeWidth = strokeWidth * 2;

          return (
            <div
              key={video.id}
              style={{
                position: "absolute",
                top:
                  roundToHalfPixel(video.position.y * previewHeight) -
                  strokeWidth,
                left:
                  roundToHalfPixel(video.position.x * previewWidth) -
                  strokeWidth,
                width:
                  roundToHalfPixel(video.size.width * previewWidth) +
                  doubleStrokeWidth,
                height:
                  roundToHalfPixel(video.size.height * previewHeight) +
                  doubleStrokeWidth,
                zIndex: video.layerIndex,
                transformOrigin: "center",
                transform: video.rotation
                  ? `rotate(${video.rotation}deg)`
                  : undefined,
              }}
            >
              {getVideoComponent(video)}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <>
      {templateVideos.map((video) => {
        return (
          <DraggableComponent
            key={video.id}
            position={video.position}
            size={video.size}
            rotation={video.rotation}
            layerIndex={video.layerIndex}
            onDragStop={(params) => updateVideo(params)}
            onRotate={(rotation) => updateVideo({ id: video.id, rotation })}
            saveRatio={true}
            drag={true}
            resize={true}
            mask={video.mask}
            onMask={(mask) => updateVideo({ id: video.id, mask })}
            item={{
              type: ITEM_TYPES.Video,
              id: video.id,
            }}
            onFocus={() => {
              if (
                activeTab !== TabKeys.objects &&
                activeTab !== TabKeys.motion
              ) {
                setActiveTab(TabKeys.objects);
              }
            }}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
            stroke={video.stroke}
          >
            <AnimationPreviewWrapper
              animations={video.animations}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              itemId={video.id}
              rotation={video.rotation}
            >
              {getVideoComponent(video)}
            </AnimationPreviewWrapper>
          </DraggableComponent>
        );
      })}
    </>
  );
};
